import { assign, type ActionArgs as XStateActionArgs } from 'xstate';

import { VerifiedGroupStatus } from '@sg/graphql-schema';

import { hasPersistentIdMeSession } from '../../config';
import { logger } from './logger';

import type {
  IdMeAuthorizationRequest,
  IdMeAuthorizationResult,
  IdMeSignOutResult,
} from '../../idme.types';
import type { IdMeVerificationEvent } from './events';
import type { IdMeVerificationContext, VerificationResult, VerifiedGroups } from './types';

// ─── Internal Actions ────────────────────────────────────────────────────────

export const clearAuthorization: IdMeVerificationAction = assign({
  request: undefined,
  authorizationCode: undefined,
});

export const assignVerifiedGroups: IdMeVerificationAction<VerifiedGroupsParams> = assign(
  (_args, params) => {
    return { verifiedGroups: getActiveGroups(params.verifiedGroups) };
  },
);

export const assignRequest: IdMeVerificationAction<RequestParams> = assign((_args, params) => {
  return { request: params.request };
});

export const assignAuthorizationCode: IdMeVerificationAction<AuthorizationResultParams> = assign(
  (_args, params) => {
    const { result } = params;

    if (result.status !== 'authorized') {
      return {};
    }

    return { authorizationCode: result.code };
  },
);

export const assignVerifiedGroupsFromResult: IdMeVerificationAction<VerificationResultParams> =
  assign((_args, params) => {
    const { verificationResult } = params;

    if (verificationResult.__typename !== 'VerifyGroupSuccess') {
      return {};
    }

    return { verifiedGroups: getActiveGroups(verificationResult.verifiedGroups) };
  });

export const assignIdMeSession: IdMeVerificationAction = assign({
  hasIdMeSession: hasPersistentIdMeSession,
});

export const assignSignOutResult: IdMeVerificationAction<SignOutResultParams> = assign(
  (_args, params) => {
    return { hasIdMeSession: params.signOutResult.status === 'cancelled' };
  },
);

export const logVerifiedGroupsFailure: IdMeVerificationAction<FailureParams> = (_args, params) => {
  logger.error('Verified Groups Fetch Failed', { error: getErrorMessage(params.error) });
};

export const logRequestFailure: IdMeVerificationAction<FailureParams> = (_args, params) => {
  logger.error('Authorization Request Failed', { error: getErrorMessage(params.error) });
};

export const logAuthorizationFailure: IdMeVerificationAction<AuthorizationResultParams> = (
  _args,
  params,
) => {
  const { result } = params;

  if (result.status !== 'failed') {
    logger.error('Authorization Failed', { status: result.status });

    return;
  }

  logger.error('Authorization Failed', { code: result.code, description: result.description });
};

export const logVerificationFailure: IdMeVerificationAction<FailureParams> = (_args, params) => {
  logger.error('Verification Failed', { error: getErrorMessage(params.error) });
};

export const logSignOutFailure: IdMeVerificationAction<FailureParams> = (_args, params) => {
  logger.error('Sign Out Failed', { error: getErrorMessage(params.error) });
};

// ─── External Actions ────────────────────────────────────────────────────────

export const showCancelledNotice: IdMeVerificationAction = noop;
export const showFailedNotice: IdMeVerificationAction = noop;
export const showNotVerifiedNotice: IdMeVerificationAction = noop;
export const showLinkConflictNotice: IdMeVerificationAction<VerificationResultParams> = noop;

export const trackVerifiedGroups: IdMeVerificationAction<VerifiedGroupsParams> = noop;
export const trackVerifyTapped: IdMeVerificationAction = noop;
export const trackVerificationStarted: IdMeVerificationAction = noop;
export const trackVerificationSucceeded: IdMeVerificationAction<VerificationResultParams> = noop;
export const trackVerificationFailed: IdMeVerificationAction<FailureReasonParams> = noop;
export const trackAuthorizationFailure: IdMeVerificationAction<AuthorizationResultParams> = noop;
export const trackVerificationAbandoned: IdMeVerificationAction = noop;
export const trackAccountConflict: IdMeVerificationAction = noop;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getActiveGroups(verifiedGroups: VerifiedGroups) {
  return verifiedGroups.filter(checkIfGroupActive);
}

function checkIfGroupActive(verifiedGroup: VerifiedGroups[number]) {
  return verifiedGroup.status === VerifiedGroupStatus.Active;
}

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

function noop() {
  return;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type IdMeVerificationAction<ActionParams extends Record<string, unknown> | void = void> = (
  actionArgs: XStateActionArgs<
    IdMeVerificationContext,
    IdMeVerificationEvent,
    IdMeVerificationEvent
  >,
  params: ActionParams,
) => void;

type FailureParams = {
  error: unknown;
};

type VerifiedGroupsParams = {
  verifiedGroups: VerifiedGroups;
};

type RequestParams = {
  request: IdMeAuthorizationRequest;
};

type AuthorizationResultParams = {
  result: IdMeAuthorizationResult;
};

type VerificationResultParams = {
  verificationResult: VerificationResult;
};

type SignOutResultParams = {
  signOutResult: IdMeSignOutResult;
};

type FailureReasonParams = {
  failureReason: string;
};
